import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, Image as ImageIcon, Tag, X } from 'lucide-react';

/**
 * One-time welcome banner shown to a user whose account is fresh
 * (`is_first_login: true`). Three quick links jump straight to the relevant
 * Settings panel — the dismiss button calls /api/users/me/dismiss-welcome
 * via AuthContext so the banner never returns.
 */
export default function WelcomeBanner() {
  const { user, dismissWelcome, canManage } = useAuth();
  const navigate = useNavigate();
  if (!user?.is_first_login || !canManage) return null;

  const goSettings = (anchor) => () => {
    navigate(`/settings${anchor ? `#${anchor}` : ''}`);
  };

  const action = (Icon, label, onClick, testId) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div
      className="relative bg-gradient-to-r from-[#1A3E6F] to-[#15325A] text-white rounded-2xl px-5 py-4 shadow-sm mb-6"
      data-testid="welcome-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pr-8">
        <p className="text-sm font-medium leading-snug max-w-xl">
          Welcome to <span className="font-semibold">satisFIND®</span>! Get started by setting your company name, adding your first branch, and uploading your logo.
        </p>
        <div className="flex flex-wrap gap-2 sm:ml-auto shrink-0">
          {action(Tag, 'Set Company Name', goSettings('company'), 'welcome-set-company-btn')}
          {action(Building2, 'Add Branch', () => navigate('/branches'), 'welcome-add-branch-btn')}
          {action(ImageIcon, 'Upload Logo', goSettings('branding'), 'welcome-upload-logo-btn')}
        </div>
      </div>
      <button
        type="button"
        onClick={dismissWelcome}
        aria-label="Dismiss welcome banner"
        data-testid="welcome-dismiss-btn"
        className="absolute top-3 right-3 p-1 rounded-md hover:bg-white/15 transition-colors"
      >
        <X className="w-4 h-4 text-white/80" />
      </button>
    </div>
  );
}
